import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtMoneyFull, fmtPct, fmtDate, STAGES, ALL_STAGES } from "../lib/format";
import { X, PaperPlaneTilt, ChatCircleText, ArrowsClockwise, Flame, Megaphone, MagnifyingGlass, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import ClosingPipeline from "./ClosingPipeline";

export default function LeadDrawer({ leadId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("thread");
  const [outSubject, setOutSubject] = useState("");
  const [outBody, setOutBody] = useState("");
  const [attachIds, setAttachIds] = useState([]);
  const [uploads, setUploads] = useState([]);   // [{filename, content_base64}]
  const [docs, setDocs] = useState([]);

  const load = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [r, dr] = await Promise.all([api.get(`/leads/${leadId}`), api.get(`/leads/${leadId}/documents`).catch(() => ({ data: [] }))]);
      setData(r.data);
      setDocs(dr.data || []);
    } catch { toast.error("Failed to load lead"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  if (!leadId) return null;
  const lead = data?.lead;
  const emails = data?.emails || [];

  const toggleAttach = (id) => {
    setAttachIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const onUpload = (e) => {
    const files = Array.from(e.target.files || []);
    Promise.all(files.map(f => new Promise((res) => {
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = String(reader.result || "").split(",")[1] || "";
        res({ filename: f.name, content_base64: b64 });
      };
      reader.readAsDataURL(f);
    }))).then(list => setUploads(prev => [...prev, ...list]));
    e.target.value = "";
  };
  const sendReply = async () => {
    if (!outBody.trim()) { toast.error("Write a message first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/reply`, {
        subject: outSubject.trim() || null,
        text: outBody,
        attach_doc_ids: attachIds,
        upload_attachments: uploads,
      });
      if (r.data.status === "sent") toast.success(`Reply sent to ${r.data.to}${r.data.attachments ? ` (${r.data.attachments} attachments)` : ""}`);
      else toast.warning(`Reply ${r.data.status}`);
      setOutSubject(""); setOutBody(""); setAttachIds([]); setUploads([]);
      await load(); onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed");
    } finally { setBusy(false); }
  };

  const sendOffer = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/send-offer`);
      toast.success(r.data.status === "sent" ? "Offer sent" : "Offer queued (mock mode)");
      await load(); onUpdated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const followUp = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/followup`);
      toast.success("Follow-up dispatched");
      await load(); onUpdated?.();
    } catch { toast.error("Follow-up failed"); }
    finally { setBusy(false); }
  };
  const simulate = async () => {
    if (!replyText.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/simulate-reply`, { lead_id: leadId, text: replyText });
      toast.success(`Reply classified: ${r.data.category} → ${r.data.new_stage.replace("_", " ")}`);
      setReplyText(""); await load(); onUpdated?.();
    } catch { toast.error("Reply failed"); }
    finally { setBusy(false); }
  };
  const setStage = async (stage) => {
    try {
      await api.patch(`/leads/${leadId}/stage`, { stage });
      toast.success("Stage updated");
      await load(); onUpdated?.();
    } catch { toast.error("Update failed"); }
  };
  const blastToBuyers = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/buyer-blast`);
      const { matched, sent, mock_sent, failed, match_mode } = r.data;
      if (matched === 0) toast.warning("No matching cash buyers in your list for this ZIP/state + property type + price gate.");
      else toast.success(`Buyer blast (${match_mode}): ${matched} matched · ${sent} sent · ${mock_sent} mock · ${failed} failed`);
      await load(); onUpdated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Buyer blast failed"); }
    finally { setBusy(false); }
  };
  const buyContact = async () => {
    if (!window.confirm("Run BatchData skip-trace on this lead? This uses your BatchData wallet balance.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/leads/${leadId}/buy-contact`);
      if (r.data.email) toast.success(`Contact bought: ${r.data.email}${r.data.auto_offer ? ` · offer ${r.data.auto_offer}` : ""}`);
      else if (r.data.error) toast.error(`Skip-trace: ${r.data.error}`);
      else toast.warning("No contact returned by BatchData for this address.");
      await load(); onUpdated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Buy Contact failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#0c0c0f] border-l border-zinc-800 flex flex-col ml-auto">
        <div className="p-5 border-b border-zinc-800 flex items-start justify-between">
          <div className="min-w-0">
            <div className="overline">Lead Detail</div>
            <div className="text-lg font-semibold tracking-tight mt-1 truncate">{lead?.address || "…"}</div>
            <div className="text-xs text-zinc-500 mono truncate">{lead?.city}, {lead?.state} {lead?.zip_code}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-900 rounded-sm" data-testid="drawer-close"><X size={18} /></button>
        </div>

        {loading || !lead ? (
          <div className="p-6 text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="flex-1 overflow-y-auto scroll-y">
            <div className="p-5 grid grid-cols-2 gap-4 border-b border-zinc-800">
              <Info label="Property Type" value={lead.property_type} />
              <Info label="Owner" value={lead.owner_name || "Unknown"} />
              <Info label="Est. Value" value={fmtMoneyFull(lead.estimated_value)} mono />
              <Info label="Equity" value={fmtPct(lead.equity_percentage)} mono accent="#34d399" />
              <Info label="MAO (70% ARV)" value={fmtMoneyFull(lead.offer_amount)} mono />
              <Info label="Proj. Profit" value={fmtMoneyFull(lead.projected_profit)} mono accent="#34d399" />
              <Info label="Owner Email" value={lead.owner_email || "not on file"} small />
              <Info label="Follow-ups" value={lead.follow_up_count} mono />
              <Info label="Last Contact" value={fmtDate(lead.last_contacted_at)} mono />
            </div>

            <div className="p-5 border-b border-zinc-800">
              <div className="overline mb-2">Move Card</div>
              <div className="flex flex-wrap gap-2">
                {ALL_STAGES.map(s => (
                  <button key={s.key} onClick={() => setStage(s.key)}
                    data-testid={`stage-btn-${s.key}`}
                    className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border transition-colors ${lead.stage === s.key ? "bg-zinc-800 border-zinc-600 text-zinc-100" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-5 border-b border-zinc-800">
              <div className="overline mb-3">Actions</div>
              <div className="flex flex-wrap gap-2">
                <button onClick={sendOffer} disabled={busy} data-testid="btn-send-offer"
                  className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center gap-2 disabled:opacity-50">
                  <PaperPlaneTilt size={13} weight="bold" /> Send Cash Offer
                </button>
                <button onClick={followUp} disabled={busy} data-testid="btn-followup"
                  className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-zinc-700 hover:border-zinc-500 flex items-center gap-2 disabled:opacity-50">
                  <ArrowsClockwise size={13} /> Follow-up now
                </button>
                <button onClick={blastToBuyers} disabled={busy} data-testid="btn-blast-buyers"
                  className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 flex items-center gap-2 disabled:opacity-50">
                  <Megaphone size={13} weight="bold" /> Blast to Buyers
                </button>
                {lead.manual_skip_trace_pending && (
                  <button onClick={buyContact} disabled={busy} data-testid="btn-buy-contact"
                    className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-violet-500/60 bg-violet-500/10 hover:bg-violet-500/20 text-violet-200 flex items-center gap-2 disabled:opacity-50">
                    <MagnifyingGlass size={13} weight="bold" /> Buy Contact
                  </button>
                )}
              </div>
              {lead.source === "zillow" && (
                <div className="mt-3 text-[11px] mono text-zinc-500">
                  Source: <span className="text-violet-300">Zillow</span>
                  {lead.days_on_market ? <> · <span className="text-zinc-400">{lead.days_on_market} days on market</span></> : null}
                  {lead.listing_url ? <> · <a href={lead.listing_url} target="_blank" rel="noreferrer" className="text-violet-300 hover:underline">listing ↗</a></> : null}
                  {lead.motivated_keywords?.length ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {lead.motivated_keywords.map(k => (
                        <span key={k} className="text-[9px] mono uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10">{k}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div className="p-5 border-b border-zinc-800">
              <div className="overline mb-3 flex items-center gap-1.5"><ChatCircleText size={12} /> Communication</div>
              <div className="flex gap-2 mb-3" data-testid="drawer-comm-tabs">
                <button onClick={() => setTab("thread")} data-testid="tab-thread"
                  className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border ${tab === "thread" ? "bg-zinc-800 border-zinc-600 text-zinc-100" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                  Thread · {emails.length}
                </button>
                <button onClick={() => setTab("reply")} data-testid="tab-reply"
                  className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border ${tab === "reply" ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                  Reply
                </button>
                <button onClick={() => setTab("simulate")} data-testid="tab-simulate"
                  className={`text-[11px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border ${tab === "simulate" ? "bg-violet-500/15 border-violet-500/50 text-violet-300" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                  Simulate inbound
                </button>
              </div>

              {tab === "reply" && (
                <div className="space-y-2.5" data-testid="reply-composer">
                  <input value={outSubject} onChange={e => setOutSubject(e.target.value)} data-testid="reply-subject"
                    placeholder={`Re: [URGENT] Cash Offer for ${lead.address}`}
                    className="w-full bg-[#18181b] hairline px-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
                  <textarea value={outBody} onChange={e => setOutBody(e.target.value)} rows={5} data-testid="reply-body"
                    placeholder={`Hello ${lead.owner_name || "there"}, thanks for getting back to me. I can move on the price you mentioned. Attached is the Purchase Agreement — please sign and reply.`}
                    className="w-full bg-[#18181b] hairline px-3 py-2 mono text-sm rounded-sm focus:outline-none focus:border-emerald-500" />

                  {docs.length > 0 && (
                    <div>
                      <div className="overline mb-1.5 flex items-center gap-1.5"><Paperclip size={11} /> Attach existing docs</div>
                      <div className="flex flex-wrap gap-2">
                        {docs.map(d => {
                          const on = attachIds.includes(d.id);
                          return (
                            <button key={d.id} type="button" onClick={() => toggleAttach(d.id)} data-testid={`attach-doc-${d.id}`}
                              className={`text-[10px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border truncate max-w-[240px] ${on ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
                              {on ? "✓ " : ""}{d.kind === "purchase_agreement" ? "PA" : d.kind === "assignment" ? "Assign" : d.kind}: {d.filename}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-[10px] mono uppercase tracking-[0.15em] text-zinc-400 inline-flex items-center gap-1.5 cursor-pointer border border-zinc-800 hover:border-zinc-600 px-2.5 py-1.5 rounded-sm">
                      <Paperclip size={11} /> Upload files
                      <input type="file" multiple onChange={onUpload} data-testid="reply-upload" className="hidden" accept=".pdf,image/*" />
                    </label>
                    {uploads.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {uploads.map((u, i) => (
                          <span key={i} className="text-[10px] mono text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 rounded-sm inline-flex items-center gap-1.5">
                            {u.filename}
                            <button onClick={() => setUploads(prev => prev.filter((_, ix) => ix !== i))} className="text-emerald-400 hover:text-red-400">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <button onClick={sendReply} disabled={busy || !outBody.trim()} data-testid="btn-send-reply"
                    className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex items-center gap-2 disabled:opacity-40">
                    <PaperPlaneTilt size={13} weight="bold" /> {busy ? "Sending…" : `Send to ${lead.owner_email || "…"}`}
                  </button>
                  <div className="text-[10px] mono text-zinc-500">Human-authored reply · CAN-SPAM footer + unsubscribe injected automatically.</div>
                </div>
              )}

              {tab === "simulate" && (
                <div className="space-y-2" data-testid="simulate-tab">
                  <textarea data-testid="simulate-reply-input"
                    value={replyText} onChange={(e) => setReplyText(e.target.value)}
                    placeholder="e.g. Yes, I'm interested. When can we close?"
                    rows={3}
                    className="w-full bg-[#18181b] hairline text-zinc-100 px-3 py-2 mono text-sm focus:outline-none focus:border-violet-500 rounded-sm" />
                  <button onClick={simulate} disabled={busy || !replyText.trim()} data-testid="btn-simulate"
                    className="text-xs mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border border-violet-500/60 bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 flex items-center gap-2 disabled:opacity-40">
                    <Flame size={13} /> Classify &amp; route
                  </button>
                  <div className="text-[10px] mono text-zinc-500">Claude Sonnet 5 auto-routes reply. "Yes" triggers HOT DEAL handoff + auto-Purchase Agreement.</div>
                </div>
              )}

              {tab === "thread" && (
                <ul className="space-y-3" data-testid="drawer-thread">
                  {emails.length === 0 && <li className="text-sm text-zinc-500">No messages yet — hit Reply to open the conversation.</li>}
                  {emails.map(e => (
                    <li key={e.id} className={`border rounded-sm p-3 ${e.direction === "out" ? "border-zinc-800 bg-[#111114]" : "border-emerald-500/30 bg-emerald-500/5"}`}>
                      <div className="flex items-center justify-between text-[10px] mono uppercase tracking-[0.15em] mb-1.5">
                        <span className={e.direction === "out" ? "text-zinc-500" : "text-emerald-400"}>
                          {e.direction === "out" ? "Outbound" : "Inbound"} · {e.status}
                        </span>
                        <span className="text-zinc-500">{fmtDate(e.created_at)}</span>
                      </div>
                      <div className="text-sm font-medium mb-1">{e.subject}</div>
                      <div className="text-xs text-zinc-400 mono whitespace-pre-wrap">{e.body_text}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <ClosingPipeline lead={lead} onUpdated={load} />
          </div>
        )}
      </div>
    </div>
  );
}

const Info = ({ label, value, mono, accent, small }) => (
  <div>
    <div className="overline">{label}</div>
    <div className={`${mono ? "mono" : ""} ${small ? "text-xs" : "text-sm"} mt-0.5 truncate`} style={accent ? { color: accent } : undefined}>{value ?? "—"}</div>
  </div>
);
